import { Geolocation } from '@capacitor/geolocation'
import { Haptics, ImpactStyle } from '@capacitor/haptics'
import { useState } from 'react'

import BLE from '../plugins/BLEPlugin'
import { logEvent } from '../plugins/Intercom'
import request from '../request'
import { putLogEvents } from '../services/aws'

const sendBluetoothWarning = async (message: string) => {
  try {
    const { coords } = await Geolocation.getCurrentPosition({ enableHighAccuracy: true })

    request('/customer/warnings', 'POST', { coordinates: coords, message })
  } catch (e) {
    console.log(e)
  }
}

type MatchedLocks = (Lock & EasyAccess & { remoteAccess?: boolean })[]

type Data = { matchedLocks?: MatchedLocks; matchedTestPeripherals?: string[]; activeLockName?: string; action?: string } | null

export default function useQlocxDevices(
  customerId: string,
  isTester: boolean,
  locks: (Lock & EasyAccess)[]
): [
  () => void,
  (obj: BLE & { lockName: string }) => void,
  (peripheral: string) => void,
  (obj: { key: string; lockName: string }) => void,
  (obj: { lockId: string; lockName: string }) => Promise<{ successful?: boolean }>,
  (lockId: string, doorName: string) => Promise<void>,
  (shouldUseTimeout?: boolean) => void,
  { loading: boolean; error: Error | null; data: Data }
] {
  const [loading, setLoading] = useState(false)
  const [data, setData] = useState<Data>(null)
  const [error, setError] = useState<Error | null>(null)
  const [, setScanHistory] = useState<Date[]>([])

  const matchLocksWithScanResult = (peripherals: string[]) => {
    const matchedLocks: MatchedLocks = []

    locks.forEach((lock) => {
      const lockUser = lock.users?.find((user) => user.customer === customerId)
      if (lock.qiot && (lockUser?.remoteAccess || lockUser?.type === 'contactPerson')) {
        matchedLocks.push({ ...lock, remoteAccess: true })
      }

      if (lock.carrier_door && (lockUser?.remoteCarrierDoorAccess || lockUser?.type === 'contactPerson')) {
        const carrierDoorLock = locks.find((l) => l._id === lock.carrier_door)

        if (carrierDoorLock?.qiot) {
          matchedLocks.push({ ...carrierDoorLock, remoteAccess: true })
        }
      }

      const compareStr = lock.ble_name || (lock.ble?.command && lock.ble?.public_key?.slice(0, 8))

      if (peripherals.some((peripheral) => peripheral === compareStr)) {
        matchedLocks.push(lock)
      }

      if ((lock.doors || []).length > 0) {
        peripherals.forEach((peripheral) => {
          lock.doors?.forEach((door) => {
            const compareString = lock.ble?.public_key?.slice(0, 8) === peripheral || lock.controllers?.[door.controller_index || 0]?._id?.public_key?.slice(0, 8) === peripheral
            if (compareString) {
              matchedLocks.push({
                ...lock,
                _id: door._id,
                custom_name: door.display_name,
              })
            }
          })
        })
      }
    })

    return matchedLocks
  }

  const resetState = (shouldUseTimeout = true) => {
    setTimeout(
      () => {
        setLoading(false)
        setData(null)
        setError(null)
      },
      shouldUseTimeout ? 3000 : 0
    )
  }

  const scan = async () => {
    setLoading(true)
    setError(null)
    setData({ action: 'scan' })

    setScanHistory((prev: Date[]) => {
      const now = new Date()
      const newHistory = prev
        .filter((h) => {
          const diff = h.getTime() - now.getTime()

          return diff < 60000
        })
        .concat(now)

      if (newHistory.length === 3) {
        sendBluetoothWarning('Scanned 3 times in 1 minute')
        return []
      }

      return newHistory
    })

    Haptics.impact({ style: ImpactStyle.Light })
    logEvent('scan_start')

    try {
      const { logEntries, peripherals } = await BLE.getQlocxPeripherals()
      putLogEvents('customer', customerId, logEntries, 'getQlocxPeripherals', true)

      if (isTester) {
        if (peripherals.length === 0) {
          throw new Error('No locks found')
        } else {
          setData({ action: 'scan', matchedTestPeripherals: peripherals.filter((p) => /^[0-9A-Fa-f]{8}$/.test(p)) })
        }
      } else {
        const matchedLocks = matchLocksWithScanResult(peripherals)

        if (matchedLocks.length === 0) {
          throw new Error('No locks found')
        }

        setData({ action: 'scan', matchedLocks })
      }
    } catch (e: any) {
      putLogEvents('customer', customerId, e.logEntries, 'getQlocxPeripherals', false, e.message)
      logEvent('scan_error', { message: e.message })
      setError(e as Error)
    } finally {
      setLoading(false)
    }
  }

  const open = async (obj: BLE & { lockName: string }) => {
    setLoading(true)
    setError(null)
    setData({ action: 'open', activeLockName: obj.lockName })

    try {
      const { logEntries, result } = await BLE.executePayloads(obj)
      request('/events', 'POST', result)

      Haptics.impact({ style: ImpactStyle.Light })

      logEvent('lock_opened', { publicKey: obj.public_key, lockName: obj.lockName, signalStrength: result[0]?.split(': ')[1] })
      putLogEvents('customer', customerId, logEntries, 'execute_payloads', true)

      resetState()
    } catch (e: any) {
      putLogEvents('customer', customerId, e.logEntries, 'execute_payloads', false, e.message)
      logEvent('lock_open_error', { publicKey: obj.public_key, lockName: obj.lockName, message: e.message })
      setError(e as Error)
    } finally {
      setLoading(false)
    }
  }

  const testOpen = async (peripheral: string) => {
    setLoading(true)
    setError(null)
    setData({ action: 'testOpen', activeLockName: peripheral })

    try {
      const r: { public_key: string; command: string } = await request(`/customer/testsuite/${peripheral}`)

      const { logEntries, result } = await BLE.executePayloads({
        public_key: r.public_key,
        peripheral,
        payloads: r.command.split('.'),
      })
      request('/events', 'POST', result)

      Haptics.impact({ style: ImpactStyle.Light })

      logEvent('lock_opened', { publicKey: r.public_key, lockName: peripheral, signalStrength: result[0]?.split(': ')[1] })
      putLogEvents('customer', customerId, logEntries, 'execute_payloads', true)
      resetState()
    } catch (e: any) {
      putLogEvents('customer', customerId, e.logEntries, 'execute_payloads', false, e.message)
      logEvent('error', { message: e.message })

      if (e.status === 400) {
        setError(new Error('Testing not permitted on this controller'))
      } else if (e.status === 404) {
        setError(new Error('Cannot find controller in the system'))
      } else {
        setError(e as Error)
      }
    } finally {
      setLoading(false)
    }
  }

  const eaOpen = async ({ key, lockName }: { key: string; lockName: string }) => {
    setLoading(true)
    setError(null)
    setData({ action: 'eaOpen', activeLockName: lockName })

    try {
      const r: { command: string; public_key: string } = await request(`/easyaccesspayload/${key}`)

      if (!r?.command || !r?.public_key) {
        throw new Error('No access found')
      }

      const { logEntries, result } = await BLE.executePayloads({
        payloads: r.command.split('.'),
        public_key: r.public_key,
        peripheral: r.public_key.slice(0, 8),
      })
      request('/events', 'POST', result)

      Haptics.impact({ style: ImpactStyle.Light })

      logEvent('lock_opened', { publicKey: r.public_key, lockName, signalStrength: result[0]?.split(': ')[1] })
      putLogEvents('customer', customerId, logEntries, 'execute_payloads', true)

      resetState()
    } catch (e: any) {
      putLogEvents('customer', customerId, e.logEntries, 'execute_payloads', false, e.message)
      logEvent('error', { message: e.message })

      if (e.status === 400) {
        setError(new Error('No access found'))
      }
      setError(e as Error)
    } finally {
      setLoading(false)
    }
  }

  const remoteOpen = async ({ lockId, lockName }: { lockId: string; lockName: string }): Promise<{ successful?: true }> => {
    setLoading(true)
    setError(null)
    setData({ action: 'remoteOpen', activeLockName: lockName })

    try {
      await request('/customer/lock/remoteaccess', 'POST', { lockId })

      Haptics.impact({ style: ImpactStyle.Light })
      logEvent('lock_opened', { publicKey: lockId, lockName, remoteAccess: true })
    } catch (e: any) {
      logEvent('error', { message: e.message })

      if (e.status === 400) {
        setError(new Error('Remote access failed. We could not connect to the lock.'))
      } else if (e.message === 'The string did not match the expected pattern.' || e.message.startsWith('Unexpected token')) {
        // workaround: inside`request()`, the response is expected to be JSON, but it's not. therefore, this specific error will be handled as if the request was successful
        Haptics.impact({ style: ImpactStyle.Light })
        logEvent('lock_opened', { publicKey: lockId, lockName, remoteAccess: true })
        resetState()
        return { successful: true }
      } else {
        setError(e as Error)
      }
    } finally {
      resetState()
    }

    return {}
  }

  const clusterOpen = async (lockId: string, doorName: string) => {
    setLoading(true)
    setError(null)
    setData({ action: 'clusterOpen', activeLockName: doorName })

    try {
      const r: { command: string; public_key: string } = await request(`/customer/doorpayload/${lockId}/${doorName}`)
      console.log('clusterOpen', r)

      if (!r?.command || !r?.public_key) {
        throw new Error('No access found')
      }

      const { logEntries, result } = await BLE.executePayloads({
        payloads: r.command.split('.'),
        public_key: r.public_key,
        peripheral: r.public_key.slice(0, 8),
      })

      Haptics.impact({ style: ImpactStyle.Light })

      logEvent('lock_opened', { publicKey: r.public_key, doorName, signalStrength: result[0]?.split(': ')[1] })

      putLogEvents('customer', customerId, logEntries, 'execute_payloads', true)

      resetState()
    } catch (e: any) {
      putLogEvents('customer', customerId, e.logEntries, 'execute_payloads', false, e.message)
      logEvent('error', { message: e.message })

      if (e.status === 400) {
        setError(new Error('No access found'))
      }

      setError(e as Error)
    } finally {
      setLoading(false)
    }
  }

  return [scan, open, testOpen, eaOpen, remoteOpen, clusterOpen, resetState, { loading, error, data }]
}
